// Core
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Styles
import './styles.scss';

import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  AudioMutedOutlined,
  PictureOutlined,
  PlaySquareOutlined,
  ReloadOutlined,
  SoundOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import { Fade } from 'react-reveal';

import { fetchQuizRound, QUESTION_COUNTS } from 'utils/quiz/api';
import { OPTION_MARK, rankFor } from 'utils/quiz/scoring';
import {
  bestVoice,
  listVoices,
  prefetchSpeech,
  preloadVoices,
  speak,
  speechSupported,
  stopSpeech,
} from 'utils/quiz/speech';

// v2: questions now come from the API, so scores from the static catalog are not comparable.
const STORAGE_KEY = 'cinemapedia-quiz-v2';
const QUESTION_SECONDS = 22;

const COPY = {
  title: { ar: 'كلاكيت', en: 'Claquette' },
  kicker: { ar: 'مسابقة السينما', en: 'CINEMA QUIZ' },
  tagline: {
    ar: 'أفلام وممثلون ومخرجون من أرشيف سينمابيديا، من الشاشة العربية إلى هوليوود وآسيا وأوروبا. أربع إجابات، وصوت يقرأ السؤال.',
    en: 'Films, actors and directors from the CinemaPedia archive, from the Arab screen to Hollywood, Asia and Europe. Four choices, with a voice that reads the question.',
  },
  library: {
    ar: 'أسئلة جديدة في كل جولة',
    en: 'A fresh set of questions every round',
  },
  start: { ar: 'ابدأ العرض', en: 'Start the show' },
  actors: { ar: 'ممثلون', en: 'Actors' },
  movies: { ar: 'أفلام', en: 'Films' },
  directors: { ar: 'مخرجون', en: 'Directors' },
  mix: { ar: 'مزيج', en: 'Mix' },
  actorsHint: { ar: 'وجوه وأدوار', en: 'Faces and roles' },
  moviesHint: { ar: 'ملصقات وسنوات وجوائز', en: 'Posters, years, awards' },
  directorsHint: { ar: 'صنّاع الأفلام', en: 'The people behind the camera' },
  mixHint: { ar: 'أسئلة منوعة', en: 'All categories' },
  questions: { ar: 'عدد الأسئلة', en: 'Questions' },
  best: { ar: 'أفضل نتيجة', en: 'Best' },
  score: { ar: 'النقاط', en: 'Score' },
  next: { ar: 'التالي', en: 'Next' },
  home: { ar: 'عودة', en: 'Back' },
  again: { ar: 'عرض آخر', en: 'Play again' },
  correct: { ar: 'إجابة صحيحة', en: 'Correct' },
  wrong: { ar: 'الإجابة الصحيحة', en: 'The answer is' },
  timeout: { ar: 'انتهى الوقت', en: 'Time is up' },
  result: { ar: 'نهاية العرض', en: 'End credits' },
  review: { ar: 'مراجعة الإجابات', en: 'Review' },
  voiceOn: { ar: 'الصوت يعمل', en: 'Voice on' },
  voiceOff: { ar: 'الصوت مغلق', en: 'Voice off' },
  speak: { ar: 'اقرأ السؤال', en: 'Read the question' },
  progress: { ar: 'سؤال', en: 'Question' },
  narrator: { ar: 'صوت الراوي', en: 'Narrator' },
  previewVoice: { ar: 'تجربة الصوت', en: 'Preview' },
  arabicVoiceNote: {
    ar: 'صوت عربي واضح يقرأ الأسئلة والإجابات.',
    en: 'A clear Arabic voice reads the questions and answers.',
  },
  loading: { ar: 'جارٍ تجهيز الأسئلة…', en: 'Setting up the reel…' },
  errorTitle: { ar: 'تعذّر تحميل المسابقة', en: 'The quiz could not be loaded' },
  retry: { ar: 'حاول مجدداً', en: 'Try again' },
  pickPicture: { ar: 'اختر الصورة الصحيحة', en: 'Pick the right picture' },
};

const SPOKEN = {
  correct: { ar: 'إجابة صحيحة.', en: 'Correct.' },
  answerIs: { ar: 'الإجابة الصحيحة هي', en: 'The correct answer is' },
  timeUp: { ar: 'انتهى الوقت.', en: 'Time is up.' },
  voiceOn: { ar: 'الصوت يعمل.', en: 'Voice on.' },
  sample: {
    ar: 'مرحباً. أنا صوت المسابقة. أيّ فيلم هذا؟',
    en: 'Hello. I am the quiz narrator. Which film is this?',
  },
};

const CATEGORIES = [
  { id: 'actors', icon: UserOutlined },
  { id: 'movies', icon: PlaySquareOutlined },
  { id: 'directors', icon: VideoCameraOutlined },
  { id: 'mix', icon: AppstoreOutlined },
];

const HINTS = {
  actors: 'actorsHint',
  movies: 'moviesHint',
  directors: 'directorsHint',
  mix: 'mixHint',
};

const ORDER_AR = ['الأولى', 'الثانية', 'الثالثة', 'الرابعة'];

const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { voice: true, best: {}, voiceUri: {}, lang: null };
    const parsed = JSON.parse(raw);
    return {
      voice: parsed.voice !== false,
      best: parsed.best || {},
      voiceUri: parsed.voiceUri || {},
      lang: parsed.lang === 'ar' || parsed.lang === 'en' ? parsed.lang : null,
    };
  } catch (e) {
    return { voice: true, best: {}, voiceUri: {}, lang: null };
  }
};

// Picture choices are read as the prompt only: the names would give the answer away.
const questionSpeech = (q, lang) => {
  const prompt = q.prompt[lang];
  if (q.optionType === 'picture') return prompt;
  if (lang === 'ar') {
    const options = q.options.map((opt, i) => `${ORDER_AR[i]}: ${opt.text.ar}`).join('. ');
    return `${prompt}. الإجابات. ${options}`;
  }
  const options = q.options
    .map((opt, i) => `Option ${OPTION_MARK.en[i]}: ${opt.text.en}`)
    .join('. ');
  return `${prompt}. ${options}`;
};

// Hides a broken remote image instead of showing the browser's broken-image icon.
const hideOnError = (event) => {
  event.currentTarget.parentElement?.classList.add('is-missing');
};

const Quiz = () => {
  const { currentLang } = useSelector((store) => store.commonData);
  // The quiz has its own language switch; the site language is only the starting point.
  const [lang, setLang] = useState(() => loadSaved().lang || (currentLang?.lang === 'en' ? 'en' : 'ar'));
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const t = useCallback((key) => COPY[key][lang], [lang]);

  const [phase, setPhase] = useState('home');
  const [voice, setVoice] = useState(true);
  const [voiceUri, setVoiceUri] = useState({});
  const [voiceList, setVoiceList] = useState([]);
  const [best, setBest] = useState({});
  const [category, setCategory] = useState('mix');
  const [count, setCount] = useState(10);
  const [round, setRound] = useState([]);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [seconds, setSeconds] = useState(QUESTION_SECONDS);
  const [timedOut, setTimedOut] = useState(false);
  const [history, setHistory] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const requestRef = useRef(0);

  const question = round[index];
  const answered = picked !== null || timedOut;
  const marks = OPTION_MARK[lang];
  const bestFor = best[category] || 0;

  const say = useCallback(
    (text) => {
      speak(text, lang, { voiceUri: voiceUri[lang] });
    },
    [lang, voiceUri]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const saved = loadSaved();
    setVoice(saved.voice);
    setBest(saved.best);
    setVoiceUri(saved.voiceUri);
    setHydrated(true);
    return () => {
      requestRef.current += 1;
      stopSpeech();
    };
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ voice, best, voiceUri, lang }));
    } catch (e) {
      /* storage unavailable */
    }
  }, [hydrated, voice, best, voiceUri, lang]);

  useEffect(() => {
    const off = preloadVoices(() => setVoiceList(listVoices(lang)));
    setVoiceList(listVoices(lang));
    if (lang === 'en') {
      const preferred = bestVoice('en');
      if (preferred) {
        setVoiceUri((prev) => (prev.en ? prev : { ...prev, en: preferred.uri }));
      }
    }
    return off;
  }, [lang]);

  // The narration language follows the site language, so stop any audio mid-flight.
  useEffect(() => {
    stopSpeech();
  }, [lang]);

  useEffect(() => {
    if (phase !== 'play' || answered) return undefined;
    const id = window.setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          setTimedOut(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, index, answered]);

  // Warm the next question's Arabic audio while the player is on this one.
  useEffect(() => {
    if (phase !== 'play' || !voice) return;
    const next = round[index + 1];
    if (next) prefetchSpeech(questionSpeech(next, lang), lang);
  }, [phase, index, round, voice, lang]);

  const start = async (nextCategory) => {
    const useCategory = nextCategory || category;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    stopSpeech();
    setCategory(useCategory);
    setErrorMessage('');
    setPhase('loading');
    window.scrollTo(0, 0);

    try {
      const built = await fetchQuizRound({ count, category: useCategory, language: lang });
      if (requestRef.current !== requestId) return; // user left or restarted meanwhile
      setRound(built);
      setIndex(0);
      setPicked(null);
      setTimedOut(false);
      setScore(0);
      setHistory([]);
      setSeconds(QUESTION_SECONDS);
      setPhase('play');
      if (voice) say(questionSpeech(built[0], lang));
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setErrorMessage(error?.message || '');
      setPhase('error');
    }
  };

  const backToHome = () => {
    requestRef.current += 1;
    stopSpeech();
    setPhase('home');
  };

  const answerLine = (q, prefix) =>
    [prefix, `${SPOKEN.answerIs[lang]} ${q.answer[lang]}`].filter(Boolean).join(' ');

  const choose = (optionId) => {
    if (answered || !question) return;
    setPicked(optionId);
    const ok = optionId === question.correctKey;
    if (ok) setScore((s) => s + 1);
    setHistory((h) => [...h, { id: question.id, correct: ok }]);
    if (!voice) return;
    say(ok ? SPOKEN.correct[lang] : answerLine(question));
  };

  // Keep the latest choose() reachable from the key handler without re-binding each render.
  const chooseRef = useRef(choose);
  chooseRef.current = choose;

  useEffect(() => {
    if (phase !== 'play') return undefined;
    const KEY_TO_OPTION = { 1: 0, 2: 1, 3: 2, 4: 3, a: 0, b: 1, c: 2, d: 3 };
    const handler = (e) => {
      const i = KEY_TO_OPTION[String(e.key).toLowerCase()];
      const q = round[index];
      if (i == null || !q || !q.options[i]) return;
      chooseRef.current(q.options[i].id);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, round, index]);

  const goNext = () => {
    if (!question) return;
    if (index + 1 >= round.length) {
      setBest((b) => {
        const prev = b[category] || 0;
        return score > prev ? { ...b, [category]: score } : b;
      });
      stopSpeech();
      setPhase('result');
      window.scrollTo(0, 0);
      if (voice) {
        say(
          lang === 'ar'
            ? `نهاية العرض. حصلت على ${score} من ${round.length}`
            : `End credits. You scored ${score} out of ${round.length}`
        );
      }
      return;
    }
    setIndex((i) => i + 1);
    setPicked(null);
    setTimedOut(false);
    setSeconds(QUESTION_SECONDS);
    const next = round[index + 1];
    if (voice && next) say(questionSpeech(next, lang));
  };

  useEffect(() => {
    if (phase !== 'play' || !timedOut || picked !== null || !question) return;
    setHistory((h) =>
      h.some((x) => x.id === question.id) ? h : [...h, { id: question.id, correct: false }]
    );
    if (voice) say(answerLine(question, SPOKEN.timeUp[lang]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, phase, picked, question]);

  const resultRank = useMemo(() => rankFor(score, round.length, lang), [score, round.length, lang]);

  const toggleVoice = () => {
    const next = !voice;
    setVoice(next);
    if (!next) {
      stopSpeech();
      return;
    }
    // Mid-round, turning voice on reads the current question instead of a confirmation.
    if (phase === 'play' && question && !answered) say(questionSpeech(question, lang));
    else say(SPOKEN.voiceOn[lang]);
  };

  const voiceToggle = (
    <button
      type="button"
      className="__quiz_chip"
      onClick={toggleVoice}
      aria-pressed={voice}
    >
      {voice ? <SoundOutlined /> : <AudioMutedOutlined />}
      {voice ? t('voiceOn') : t('voiceOff')}
    </button>
  );

  const previewVoice = () => {
    setVoice(true);
    say(SPOKEN.sample[lang]);
  };

  const optionState = (opt) => {
    if (!answered) return '';
    if (opt.id === question.correctKey) return 'is-correct';
    if (opt.id === picked) return 'is-wrong';
    return 'is-dim';
  };

  const BackArrow = dir === 'rtl' ? ArrowRightOutlined : ArrowLeftOutlined;

  const langSwitch = (
    <div className="__quiz_langs" role="group" aria-label="Quiz language">
      {[
        { code: 'ar', label: 'عربي' },
        { code: 'en', label: 'EN' },
      ].map(({ code, label }) => (
        <button
          key={code}
          type="button"
          lang={code}
          className={`__quiz_lang ${lang === code ? 'is-active' : ''}`}
          aria-pressed={lang === code}
          onClick={() => setLang(code)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="__quiz" dir={dir}>
      {phase === 'home' && (
        <div className="__quiz_home">
          <img className="__quiz_home_bg" src="/quiz/theater.jpg" alt="" />
          <div className="__quiz_home_veil" />
          <div className="container __quiz_home_inner">
            <div className="__quiz_bar">
              {langSwitch}
              {voiceToggle}
            </div>

            {voice && (
              <div className="__quiz_voice_box">
                <div className="__quiz_voice_head">
                  <span>{t('narrator')}</span>
                  <button type="button" className="__quiz_link" onClick={previewVoice}>
                    {t('previewVoice')}
                  </button>
                </div>
                {lang === 'en' && voiceList.length > 1 ? (
                  <select
                    className="__quiz_select"
                    value={voiceUri.en || voiceList[0]?.uri || ''}
                    onChange={(e) => setVoiceUri((prev) => ({ ...prev, en: e.target.value }))}
                  >
                    {voiceList.map((item) => (
                      <option key={item.uri} value={item.uri}>
                        {item.recommended ? `${item.label} — recommended` : item.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="__quiz_note">{t('arabicVoiceNote')}</p>
                )}
              </div>
            )}

            <Fade bottom cascade>
              <div className="__quiz_hero">
                <img className="__quiz_clapper" src="/quiz/clapper.jpg" alt="" />
                <p className="__quiz_kicker">{t('kicker')}</p>
                <h1 className="__quiz_title">{t('title')}</h1>
                <p className="__quiz_tagline">{t('tagline')}</p>
                <p className="__quiz_library">{t('library')}</p>
              </div>
            </Fade>

            <div className="__quiz_counts">
              <span className="__quiz_counts_label">{t('questions')}</span>
              {QUESTION_COUNTS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`__quiz_count ${count === n ? 'is-active' : ''}`}
                  onClick={() => setCount(n)}
                >
                  {n}
                </button>
              ))}
              {best[category] != null && (
                <span className="__quiz_best">
                  <TrophyOutlined /> {t('best')}: {best[category]}
                </span>
              )}
            </div>

            <div className="__quiz_cats">
              {CATEGORIES.map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`__quiz_cat ${category === id ? 'is-active' : ''}`}
                  onClick={() => start(id)}
                >
                  <Icon className="__quiz_cat_icon" />
                  <span className="__quiz_cat_name">{t(id)}</span>
                  <span className="__quiz_cat_hint">{t(HINTS[id])}</span>
                </button>
              ))}
            </div>

            <button type="button" className="__quiz_cta" onClick={() => start(category)}>
              {t('start')}
            </button>
          </div>
        </div>
      )}

      {phase === 'loading' && (
        <div className="container __quiz_status __innerTopPad">
          <Spin className="__spin_color" size="large" />
          <p className="__quiz_status_text">{t('loading')}</p>
          <button type="button" className="__quiz_link" onClick={backToHome}>
            <BackArrow /> {t('home')}
          </button>
        </div>
      )}

      {phase === 'error' && (
        <div className="container __quiz_status __innerTopPad">
          <h2 className="__quiz_prompt">{t('errorTitle')}</h2>
          {errorMessage && <p className="__quiz_status_text">{errorMessage}</p>}
          <div className="__quiz_result_actions">
            <button type="button" className="__quiz_cta" onClick={() => start(category)}>
              <ReloadOutlined /> {t('retry')}
            </button>
            <button type="button" className="__quiz_cta is-ghost" onClick={backToHome}>
              {t('home')}
            </button>
          </div>
        </div>
      )}

      {phase === 'play' && question && (
        <div className="container __quiz_play __innerTopPad">
          <div className="__quiz_play_head">
            <button type="button" className="__quiz_link" onClick={backToHome}>
              <BackArrow /> {t('home')}
            </button>
            <span className="__quiz_meta">
              {t('progress')} {index + 1} / {round.length}
            </span>
            <span className="__quiz_meta">
              {t('score')} {score}
            </span>
            {langSwitch}
            {voiceToggle}
            {voice && speechSupported() && (
              <button
                type="button"
                className="__quiz_icon_btn"
                aria-label={t('speak')}
                title={t('speak')}
                onClick={() => say(questionSpeech(question, lang))}
              >
                <ReloadOutlined />
              </button>
            )}
          </div>

          <div className="__quiz_progress">
            <div
              className="__quiz_progress_fill"
              style={{ width: `${((index + (answered ? 1 : 0)) / round.length) * 100}%` }}
            />
          </div>

          <p className={`__quiz_timer ${seconds <= 5 ? 'is-low' : ''}`}>{seconds}s</p>

          <div className="row">
            {question.image && (
              <div className="col-md-5 mb-4">
                <div key={`${question.id}-${question.image}`} className="__quiz_stage">
                  <PictureOutlined className="__quiz_pic_fallback" />
                  <img src={question.image} alt="" onError={hideOnError} />
                </div>
              </div>
            )}
            <div className={question.image ? 'col-md-7' : 'col-12'}>
              <h2 className="__quiz_prompt">{question.prompt[lang]}</h2>

              {question.optionType === 'picture' ? (
                <>
                  {!answered && <p className="__quiz_hint">{t('pickPicture')}</p>}
                  <div className="__quiz_pics">
                    {question.options.map((opt, i) => (
                      <button
                        key={`${question.id}-${opt.id}`}
                        type="button"
                        className={`__quiz_pic ${optionState(opt)}`}
                        disabled={answered}
                        onClick={() => choose(opt.id)}
                        aria-label={`${marks[i]}`}
                      >
                        <span className="__quiz_pic_frame">
                          {opt.picture ? (
                            <img src={opt.picture} alt="" onError={hideOnError} />
                          ) : null}
                          <PictureOutlined className="__quiz_pic_fallback" />
                        </span>
                        <span className="__quiz_pic_foot">
                          <span className="__quiz_option_mark">{marks[i]}</span>
                          {/* The caption names the person, so it only appears after answering. */}
                          {answered && (
                            <span className="__quiz_pic_caption">{opt.caption[lang]}</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="__quiz_options">
                  {question.options.map((opt, i) => (
                    <button
                      key={`${question.id}-${opt.id}`}
                      type="button"
                      className={`__quiz_option ${optionState(opt)}`}
                      disabled={answered}
                      onClick={() => choose(opt.id)}
                    >
                      <span className="__quiz_option_mark">{marks[i]}</span>
                      <span className="__quiz_option_text">{opt.text[lang]}</span>
                    </button>
                  ))}
                </div>
              )}

              {answered && (
                <div className="__quiz_reveal">
                  <p
                    className={`__quiz_verdict ${
                      picked === question.correctKey ? 'is-correct' : 'is-wrong'
                    }`}
                  >
                    {picked === question.correctKey
                      ? t('correct')
                      : `${timedOut ? `${t('timeout')} · ` : ''}${t('wrong')}: ${
                          question.answer[lang]
                        }`}
                  </p>
                  <button type="button" className="__quiz_cta" onClick={goNext}>
                    {index + 1 >= round.length ? t('result') : t('next')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === 'result' && (
        <div className="container __quiz_result __innerTopPad">
          <Fade bottom cascade>
            <div>
              <p className="__quiz_kicker">{t('result')}</p>
              <h1 className="__quiz_rank">{resultRank}</h1>
              <p className="__quiz_score">
                {score} / {round.length}
              </p>
              {bestFor > 0 && (
                <p className="__quiz_best">
                  <TrophyOutlined /> {t('best')}: {Math.max(bestFor, score)}
                </p>
              )}
            </div>
          </Fade>

          <h2 className="__quiz_review_head">{t('review')}</h2>
          <ul className="__quiz_review">
            {round.map((q, i) => {
              const ok = history.find((h) => h.id === q.id)?.correct;
              const thumb = q.image || q.answerPicture;
              return (
                <li key={q.id} className="__quiz_review_row">
                  <span className="__quiz_review_thumb">
                    {thumb ? <img src={thumb} alt="" onError={hideOnError} /> : null}
                    <PictureOutlined className="__quiz_pic_fallback" />
                  </span>
                  <div className="__quiz_review_body">
                    <p className="__quiz_review_answer">{q.answer[lang]}</p>
                    <p className="__quiz_review_prompt">{q.prompt[lang]}</p>
                  </div>
                  <span className={`__quiz_review_flag ${ok ? 'is-correct' : 'is-wrong'}`}>
                    {ok ? (lang === 'ar' ? 'صح' : 'Yes') : lang === 'ar' ? 'خطأ' : 'No'}
                  </span>
                  <span className="__quiz_review_no">{i + 1}</span>
                </li>
              );
            })}
          </ul>

          <div className="__quiz_result_actions">
            <button type="button" className="__quiz_cta" onClick={() => start(category)}>
              <ReloadOutlined /> {t('again')}
            </button>
            <button type="button" className="__quiz_cta is-ghost" onClick={backToHome}>
              {t('home')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quiz;
